import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import API_URL from '../config';

function getAuthHeader() {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const api = {
  list: (params) =>
    axios.get(`${API_URL}/api/email-templates`, {
      params,
      headers: getAuthHeader(),
    }).then((r) => r.data),

  get: (id) =>
    axios.get(`${API_URL}/api/email-templates/${id}`, {
      headers: getAuthHeader(),
    }).then((r) => r.data),

  create: (data) =>
    axios.post(`${API_URL}/api/email-templates`, data, {
      headers: getAuthHeader(),
    }).then((r) => r.data),

  update: (id, data) =>
    axios.put(`${API_URL}/api/email-templates/${id}`, data, {
      headers: getAuthHeader(),
    }).then((r) => r.data),

  toggleActive: (id) =>
    axios.post(`${API_URL}/api/email-templates/${id}/toggle-active`, {}, {
      headers: getAuthHeader(),
    }).then((r) => r.data),

  preview: (id, sampleData) =>
    axios.post(`${API_URL}/api/email-templates/${id}/preview`, { sampleData }, {
      headers: getAuthHeader(),
    }).then((r) => r.data),

  previewRaw: (data) =>
    axios.post(`${API_URL}/api/email-templates/preview-raw`, data, {
      headers: getAuthHeader(),
    }).then((r) => r.data),

  mergeFields: () =>
    axios.get(`${API_URL}/api/email-templates/merge-fields`, {
      headers: getAuthHeader(),
    }).then((r) => r.data),

  sampleData: () =>
    axios.get(`${API_URL}/api/email-templates/sample-data`, {
      headers: getAuthHeader(),
    }).then((r) => r.data),

  reservations: () =>
    axios.get(`${API_URL}/api/email-templates/reservations`, {
      headers: getAuthHeader(),
    }).then((r) => r.data),

  reservationContext: (reservationId) =>
    axios.get(`${API_URL}/api/email-templates/reservation-context/${reservationId}`, {
      headers: getAuthHeader(),
    }).then((r) => r.data),
};

export function useTemplates(filters = {}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['email-templates', filters],
    queryFn: () => api.list(filters),
  });

  return {
    templates: data?.data || [],
    total: data?.total || 0,
    isLoading,
    error,
  };
}

export function useTemplate(id) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['email-template', id],
    queryFn: () => api.get(id),
    enabled: !!id,
  });

  return {
    template: data?.data || null,
    isLoading,
    error,
  };
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.create(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['email-templates'] }),
  });
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) => api.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      queryClient.invalidateQueries({ queryKey: ['email-template', variables.id] });
    },
  });
}

export function useToggleTemplateActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.toggleActive(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['email-templates'] }),
  });
}

export function useMergeFields() {
  const { data, isLoading } = useQuery({
    queryKey: ['email-merge-fields'],
    queryFn: () => api.mergeFields(),
    staleTime: 5 * 60 * 1000,
  });

  return { groups: data?.data?.groups || [], isLoading };
}

export function useSampleData() {
  const { data, isLoading } = useQuery({
    queryKey: ['email-sample-data'],
    queryFn: () => api.sampleData(),
    staleTime: 5 * 60 * 1000,
  });

  return { sampleData: data?.data || null, isLoading };
}

export function useReservationList() {
  const { data, isLoading } = useQuery({
    queryKey: ['email-reservations'],
    queryFn: () => api.reservations(),
    staleTime: 2 * 60 * 1000,
  });

  return { reservations: data?.data || [], isLoading };
}

export function useReservationContext(reservationId) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['email-reservation-context', reservationId],
    queryFn: () => api.reservationContext(reservationId),
    enabled: !!reservationId,
  });

  return { context: data?.data || null, isLoading, error };
}

export { api as templateApi };
